import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, Star, Feather } from "lucide-react";

import { _t } from "@/lib/i18n";
export default function RaccontiAuthorPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/racconti/authors/${slug}`); setData(r.data); }
      catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [slug]);

  if (!data) return <AppLayout><div className="text-white/50">{_t("Carico autore…")}</div></AppLayout>;
  const a = data.author;

  return (
    <AppLayout>
      <div className="space-y-8 max-w-4xl mx-auto">
        <header className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 space-y-2">
          <div className="flex items-center gap-3">
            <Feather className="w-10 h-10 text-amber-300" />
            <div>
              <h1 className="text-3xl text-white font-display" data-testid="author-name">{a.pen_name}</h1>
              <div className="text-xs text-white/50">{_t("{0} capitoli · {1} download totali", a.chapters_published, a.downloads_total)}</div>
            </div>
          </div>
          {a.bio && <p className="text-white/80 text-sm whitespace-pre-wrap leading-relaxed pt-2">{a.bio}</p>}
        </header>

        <section className="space-y-3">
          <h2 className="text-lg text-white font-display">{_t("Capitoli pubblicati")}</h2>
          {data.chapters.length === 0 ? (
            <div className="text-white/50 text-sm">{_t("Ancora nessun capitolo pubblicato.")}</div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {data.chapters.map((c) => (
                <Link key={c.id} to={`/racconti`} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 hover:border-white/25 transition-colors">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">{_t("Capitolo")}</div>
                  <div className="text-white text-lg font-display">{c.title}</div>
                  <div className="text-xs text-white/60 mt-1 line-clamp-2">{c.synopsis}</div>
                  <div className="flex gap-3 mt-2 text-xs text-white/50">
                    <span><Star className="inline w-3 h-3" /> {c.reviews_avg_rating || "—"}</span>
                    <span><BookOpen className="inline w-3 h-3" /> {c.downloads_count}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        <section className="space-y-3">
          <h2 className="text-lg text-white font-display">{_t("Bacheca dell'autore")}</h2>
          {data.wall.length === 0 ? (
            <div className="text-white/50 text-sm">{_t("Ancora nessuna recensione sulla bacheca dell'autore.")}</div>
          ) : (
            <div className="space-y-3">
              {data.wall.map((w) => (
                <div key={w.id} data-testid={`wall-${w.id}`} className={`p-4 rounded-2xl border ${w.pinned ? "border-amber-400/40 bg-amber-500/5" : "border-white/10 bg-white/[0.02]"}`}>
                  <div className="flex justify-between items-center">
                    <div className="text-white font-semibold text-sm">
                      {w.pinned && <span className="text-amber-300 mr-1">📌</span>}
                      {w.reviewer_display_name}
                    </div>
                    <div className="text-amber-300 text-xs">{"★".repeat(w.rating)}</div>
                  </div>
                  <div className="text-white/90 text-sm mt-2 whitespace-pre-wrap">{w.text}</div>
                  {w.author_reply && (
                    <div className="mt-3 pl-3 border-l-2 border-cyan-400/30 text-xs text-cyan-200">
                      <div className="text-cyan-300 mb-0.5">{_t("Risposta di {0}", a.pen_name)}</div>
                      <div className="text-white/85 whitespace-pre-wrap">{w.author_reply}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </AppLayout>
  );
}
